/**
 * @fileoverview Core Utility Functions
 * 
 * Essential utility functions used throughout the application.
 * These are foundational helpers that should remain dependency-free
 * and highly performant.
 * 
 * @module lib/utils
 * @see {@link docs/ARCHITECTURE.md} for system overview
 */

import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Merges Tailwind CSS classes with intelligent conflict resolution.
 * 
 * Combines clsx for conditional classes with tailwind-merge for
 * proper Tailwind class conflict resolution (e.g., 'p-4 p-2' → 'p-2').
 * 
 * @param inputs - Class values (strings, objects, arrays)
 * @returns Merged class string with conflicts resolved
 * 
 * @example
 * ```tsx
 * // Basic usage
 * cn('px-4 py-2', 'px-8') // → 'py-2 px-8'
 * 
 * // Conditional classes
 * cn('base-class', isActive && 'active-class')
 * 
 * // Object syntax
 * cn({ 'visible': true, 'hidden': false })
 * 
 * // With component props
 * cn(
 *   'base-styles',
 *   variants[variant],
 *   sizes[size],
 *   className
 * )
 * ```
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Generates an SEO-friendly URL slug for job listings.
 * 
 * Creates a URL-safe slug from company name, job title, and ID.
 * Format: `{company-slug}-{title-slug}-{jobId}`
 * 
 * @param companyName - Company name to slugify
 * @param jobTitle - Job title to slugify
 * @param jobId - Unique job identifier (preserved as-is)
 * @returns SEO-optimized URL slug
 * 
 * @example
 * ```ts
 * generateJobSlug('Acme Corp', 'Senior Developer', 'abc123')
 * // → 'acme-corp-senior-developer-abc123'
 * 
 * generateJobSlug('Tech & Co.', 'Full-Stack Engineer', 'xyz789')
 * // → 'tech-co-full-stack-engineer-xyz789'
 * ```
 */
export function generateJobSlug(companyName: string, jobTitle: string, jobId: string): string {
  /**
   * Converts text to URL-safe slug.
   * - Lowercase
   * - Remove special characters
   * - Replace spaces with hyphens
   * - Collapse multiple hyphens
   */
  const slugify = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .trim();
  };

  const companySlug = slugify(companyName);
  const titleSlug = slugify(jobTitle);

  return `${companySlug}-${titleSlug}-${jobId}`;
}

/**
 * Validates email address format.
 * 
 * Uses a standard regex pattern to check basic email format.
 * Does not validate if email actually exists or is deliverable.
 * 
 * @param email - Email address to validate
 * @returns True if format is valid
 * 
 * @example
 * ```ts
 * isValidEmail('user@example.com') // → true
 * isValidEmail('invalid-email') // → false
 * isValidEmail('user@domain') // → false
 * isValidEmail(' user@example.com ') // → true (trimmed)
 * ```
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
}
